import { useEffect, useState } from 'react';
import { Boxes, Filter, Search, X } from 'lucide-react';

import type { Component, Sprint, Swimlane, UserRef } from '@tasks/contracts';

import { Button } from '../../components/Button';
import { IconButton } from '../../components/IconButton';
import { Select, type SelectOption } from '../../components/Select';
import { swimlaneLabels } from '../../i18n/enums';
import styles from './BoardToolbar.module.css';

const SWIMLANE_OPTIONS: readonly SelectOption<Swimlane>[] = (
  ['none', 'assignee', 'epic', 'component'] as const
).map((value) => ({ value, label: swimlaneLabels[value] }));

export type BoardFiltersState = {
  readonly swimlane: Swimlane;
  readonly sprintId: string | null;
  readonly assigneeId: string | null;
  readonly componentId: string | null;
  readonly q: string;
  readonly onlyMine: boolean;
  readonly onlyBugs: boolean;
};

export type BoardToolbarProps = {
  readonly filters: BoardFiltersState;
  readonly sprints: readonly Sprint[];
  readonly components: readonly Component[];
  readonly members: readonly UserRef[];
  /**
   * Компоненты, которые показывает сама доска (docs/13 §7). Пустой список —
   * «все компоненты проекта»: это настройка доски, а не разовый фильтр.
   */
  readonly boardComponentIds: readonly string[];
  readonly onChange: (patch: Partial<BoardFiltersState>) => void;
  readonly onReset: () => void;
  readonly onCreateIssue: () => void;
};

const SEARCH_DEBOUNCE_MS = 300;

/** Быстрые фильтры, спринт, группировка и поиск с debounce 300 мс (UC-BRD-04). */
export function BoardToolbar({
  filters,
  sprints,
  components,
  members,
  boardComponentIds,
  onChange,
  onReset,
  onCreateIssue,
}: BoardToolbarProps) {
  const [query, setQuery] = useState(filters.q);

  useEffect(() => {
    if (query === filters.q) {
      return;
    }
    const timer = window.setTimeout(() => onChange({ q: query }), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const sprintOptions: readonly SelectOption<string>[] = sprints.map((sprint) => ({
    value: sprint.id,
    label: sprint.name,
  }));

  const assigneeOptions: readonly SelectOption<string>[] = members.map((member) => ({
    value: member.id,
    label: member.displayName,
  }));

  // Только реальные компоненты: `BoardQuerySchema.componentId` — это UUID,
  // значение `none` («без компонента») есть у публичного списка и у JQL, но не
  // у доски (docs/13 §5).
  //
  // Если доска ограничена набором (docs/13 §7), предлагаем фильтр только по
  // её компонентам: выбор компонента вне набора дал бы пустую доску без
  // объяснения. Уже открытый из ссылки фильтр вне набора показываем, чтобы
  // Select не выглядел сброшенным.
  const scopedComponents =
    boardComponentIds.length === 0
      ? components
      : components.filter((component) => boardComponentIds.includes(component.id));
  const componentOptions: readonly SelectOption<string>[] = scopedComponents.map((component) => ({
    value: component.id,
    label: component.name,
  }));
  const filterOnHiddenComponent =
    filters.componentId !== null &&
    components.some((component) => component.id === filters.componentId) &&
    !scopedComponents.some((component) => component.id === filters.componentId);
  const componentOptionsWithCurrent = filterOnHiddenComponent
    ? [
        ...componentOptions,
        {
          value: filters.componentId as string,
          label: components.find((component) => component.id === filters.componentId)?.name ?? '',
        },
      ]
    : componentOptions;

  const scopeTitle =
    boardComponentIds.length === 0
      ? ''
      : components
          .filter((component) => boardComponentIds.includes(component.id))
          .map((component) => component.name)
          .join(', ');

  const hasActiveFilters =
    filters.q.length > 0 ||
    filters.onlyMine ||
    filters.onlyBugs ||
    filters.assigneeId !== null ||
    filters.componentId !== null;

  return (
    <div className={styles['toolbar']}>
      <div className={styles['search']}>
        <Search aria-hidden size={15} />
        <input
          value={query}
          placeholder="Поиск по заголовку или ключу"
          aria-label="Поиск по доске"
          onChange={(event) => setQuery(event.target.value)}
        />
        {query.length > 0 ? (
          <IconButton
            size="sm"
            label="Очистить поиск"
            icon={<X size={14} />}
            onClick={() => {
              setQuery('');
              onChange({ q: '' });
            }}
          />
        ) : null}
      </div>

      <div className={styles['chips']}>
        <button
          type="button"
          aria-pressed={filters.onlyMine}
          className={filters.onlyMine ? styles['chipActive'] : styles['chip']}
          onClick={() => onChange({ onlyMine: !filters.onlyMine, assigneeId: null })}
        >
          Мои задачи
        </button>
        <button
          type="button"
          aria-pressed={filters.onlyBugs}
          className={filters.onlyBugs ? styles['chipActive'] : styles['chip']}
          onClick={() => onChange({ onlyBugs: !filters.onlyBugs })}
        >
          Баг
        </button>
      </div>

      <div className={styles['selects']}>
        <Select
          options={SWIMLANE_OPTIONS}
          value={filters.swimlane}
          onChange={(value) => onChange({ swimlane: value })}
          placeholder="Без группировки"
        />
        <Select
          options={sprintOptions}
          value={filters.sprintId}
          clearable
          onClear={() => onChange({ sprintId: null })}
          placeholder="Все спринты"
          onChange={(value) => onChange({ sprintId: value })}
        />
        <Select
          options={assigneeOptions}
          value={filters.assigneeId}
          clearable
          onClear={() => onChange({ assigneeId: null })}
          placeholder="Все исполнители"
          onChange={(value) => onChange({ assigneeId: value })}
        />
        {componentOptionsWithCurrent.length > 0 ? (
          <Select
            options={componentOptionsWithCurrent}
            value={filters.componentId}
            clearable
            onClear={() => onChange({ componentId: null })}
            placeholder="Все компоненты"
            onChange={(value) => onChange({ componentId: value })}
          />
        ) : null}
      </div>

      {/* Настройка доски, а не разовый фильтр: её нельзя снять из тулбара. */}
      {boardComponentIds.length > 0 ? (
        <span className={styles['scope']} title={scopeTitle}>
          <Boxes aria-hidden size={13} />
          Компоненты доски: {scopeTitle}
        </span>
      ) : null}

      {hasActiveFilters ? (
        <Button size="sm" variant="ghost" icon={<Filter size={14} />} onClick={onReset}>
          Сбросить
        </Button>
      ) : null}

      <span className={styles['spacer']} />

      <Button size="sm" variant="primary" onClick={onCreateIssue}>
        Создать
      </Button>
    </div>
  );
}
